/**
 * src/context/FormDraftContext.tsx -- WP-011 Story 2 (AC-005)
 *
 * Registry context that lets any controlled form register a draft getter
 * function. When the session expires, SessionExpiredModal calls
 * captureActiveDraft() to retrieve the currently active form values so they
 * can be persisted to sessionStorage before the modal renders.
 *
 * Design: single active slot (only one form is active at a time). Forms call
 * registerDraft on mount and unregisterDraft on unmount. The ref pattern keeps
 * the getter current on every render without causing re-registration.
 */

import React, { createContext, useCallback, useContext, useRef } from "react";

export type DraftValues = Record<string, unknown>;
type DraftGetter = () => DraftValues | null;

interface FormDraftContextValue {
  /** Called by a form on mount to register its current-state getter. */
  registerDraft: (getter: DraftGetter) => void;
  /** Called by a form on unmount to deregister. */
  unregisterDraft: () => void;
  /** Called by SessionExpiredModal to snapshot the active form state. */
  captureActiveDraft: () => DraftValues | null;
}

const FormDraftContext = createContext<FormDraftContextValue | null>(null);

interface FormDraftProviderProps {
  children: React.ReactNode;
}

export function FormDraftProvider({ children }: FormDraftProviderProps) {
  // Single active slot: only one form is "active" at any moment.
  const getterRef = useRef<DraftGetter | null>(null);

  const registerDraft = useCallback((getter: DraftGetter) => {
    getterRef.current = getter;
  }, []);

  const unregisterDraft = useCallback(() => {
    getterRef.current = null;
  }, []);

  const captureActiveDraft = useCallback((): DraftValues | null => {
    return getterRef.current?.() ?? null;
  }, []);

  return (
    <FormDraftContext.Provider
      value={{ registerDraft, unregisterDraft, captureActiveDraft }}
    >
      {children}
    </FormDraftContext.Provider>
  );
}

export function useFormDraftContext(): FormDraftContextValue {
  const ctx = useContext(FormDraftContext);
  if (!ctx) {
    throw new Error("useFormDraftContext must be used within FormDraftProvider");
  }
  return ctx;
}
